import { type ReactElement } from 'react'
import { cn } from '@util'
import { isDefined, type UserProfileData } from '@terros/common'
import { useDisclosure } from '@mantine/hooks'
import { AppShell, Box, Stack } from '@mantine/core'
import { NavigationCollapseButton } from '../navigation/CollapseButton'
import { Navigation } from '../navigation'
import { useSwipeToDismiss } from './useSwipeToDismiss'
import { useRoutes } from './useRoutes'
import { useIsMobileNav } from './useIsMobileNav'
import { useSidebarNavigationContext } from './SidebarNavigationContext'
import classes from './NavBar.module.css'
import { SidebarUserFooter } from './account/SidebarUserFooter'

type Props = {
  user?: UserProfileData
  toggleBurger: VoidFunction
  isBurgerOpen: boolean
}

export const NavBar = ({ user, toggleBurger, isBurgerOpen }: Props): ReactElement => {
  const { routes, isPending } = useRoutes()
  const [isHovered, { open: openHovered, close: closeHovered }] = useDisclosure(false)
  const [isNavbarOpen, { toggle: toggleNavbar }] = useSidebarNavigationContext()
  const isMobileNav = useIsMobileNav()
  const mobileDrawer = isBurgerOpen && isMobileNav
  const swipeDrawer = useSwipeToDismiss({ direction: 'left', onDismiss: toggleBurger })

  return (
    <AppShell.Navbar
      className={cn('border-none', mobileDrawer && 'shadow-xl', mobileDrawer && classes.mobileDrawer)}
      {...(mobileDrawer ? swipeDrawer : {})}
    >
      <Stack
        className='relative h-full min-h-0'
        gap='md'
        p='md'
        pos='relative'
        pr={mobileDrawer ? 'md' : 0}
        onMouseEnter={openHovered}
        onMouseLeave={closeHovered}
      >
        <Box className='relative min-h-0' flex={1}>
          <Navigation
            collapsible={!isBurgerOpen}
            isOpen={isNavbarOpen}
            isPending={isPending}
            routes={routes.filter((link) => isDefined(link))}
            toggle={toggleNavbar}
            toggleBurger={isBurgerOpen ? toggleBurger : undefined}
            variant='nested'
          />
          <div
            className={cn(
              'pointer-events-none absolute inset-y-0 right-0 hidden sm:block',
              isHovered ? 'opacity-100' : 'opacity-0'
            )}
          >
            <NavigationCollapseButton
              className='pointer-events-auto'
              isOpen={isNavbarOpen}
              toggle={toggleNavbar}
              visibleFrom='sm'
            />
          </div>
        </Box>
        <SidebarUserFooter
          isOpen={isNavbarOpen || mobileDrawer}
          user={user}
          onNavigate={isBurgerOpen ? toggleBurger : undefined}
        />
      </Stack>
    </AppShell.Navbar>
  )
}
